import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getCollection,
  type OrgRecord,
  type Paginated,
  type RecordAssignment,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
} from '../../components/ui.js';
import { AssignmentsDialog } from './AssignmentsDialog.js';
import './records.css';

/** Who holds which device — assignment view over the hardware inventory. */
export function ManageHardwarePage() {
  const { org } = useOrg();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  const def = getCollection('hardware')!;

  const records = useQuery({
    queryKey: ['records', org.orgId, 'hardware', 'active'],
    queryFn: () =>
      apiFetch<Paginated<OrgRecord>>(`/orgs/${org.orgId}/records`, {
        query: { collection: 'hardware', status: 'active', limit: 200 },
      }),
  });
  const assignments = useQuery({
    queryKey: ['record-assignments', org.orgId, 'collection:hardware'],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`, {
        query: { collection: 'hardware' },
      }),
  });

  const [assignFor, setAssignFor] = useState<OrgRecord | null>(null);

  const byRecord = useMemo(() => {
    const map = new Map<string, RecordAssignment[]>();
    for (const a of assignments.data?.items ?? []) {
      map.set(a.recordId, [...(map.get(a.recordId) ?? []), a]);
    }
    return map;
  }, [assignments.data]);

  if (records.isLoading || assignments.isLoading) {
    return <LoadingPane label="Loading hardware" />;
  }
  const items = records.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Manage Hardware"
        subtitle="Who holds which device — keep this current so your device inventory stands up in an assessment"
      />
      {items.length === 0 ? (
        <EmptyState
          title="No hardware recorded"
          message="Add devices in the Hardware inventory first."
          action={
            <Link to="/assets/hardware">
              <Button variant="secondary">Go to Hardware</Button>
            </Link>
          }
        />
      ) : (
        <Table
          head={
            <>
              <th>Device</th>
              <th>Type</th>
              <th>Operating system</th>
              <th>Assigned to</th>
              {canWrite && <th />}
            </>
          }
        >
          {items.map((record) => {
            const holders = byRecord.get(record.id) ?? [];
            return (
              <tr key={record.id}>
                <td style={{ fontWeight: 600 }}>
                  {record.name}
                  {(record.data.make ?? record.data.model) !== undefined && (
                    <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                      {[record.data.make, record.data.model].filter(Boolean).join(' ')}
                    </div>
                  )}
                </td>
                <td>{(record.data.type as string) || '—'}</td>
                <td>
                  {[record.data.os, record.data.osVersion].filter(Boolean).join(' ') || '—'}
                </td>
                <td>
                  {holders.length === 0 ? (
                    <Badge>Unassigned</Badge>
                  ) : (
                    holders.map((a) => (
                      <span key={a.id} className="assignee-chip">
                        <Badge tone="info">{a.userName}</Badge>
                      </span>
                    ))
                  )}
                </td>
                {canWrite && (
                  <td>
                    <div className="record-row-actions">
                      <Button variant="secondary" size="sm" onClick={() => setAssignFor(record)}>
                        Manage
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </Table>
      )}

      {assignFor && (
        <AssignmentsDialog
          def={def}
          record={assignFor}
          open={!!assignFor}
          onClose={() => setAssignFor(null)}
        />
      )}
    </div>
  );
}
